/**
 * OrgManagementRouteGuard
 * V136: 조직관리 메뉴 직책 레벨 기반 라우트 가드
 * V136.1: jobTitleId → master_jobTitles.code 기반 레벨 판별로 변경
 * V137.2: system_admin 전체 접근 권한 추가
 * V148.1: role_exec는 work-status/leave 접근 차단
 * V203.1: system_admin은 role_exec 체크보다 먼저 bypass
 *
 * RBAC 권한 외에 추가로 직책 레벨 정책을 체크하여
 * 본부장/대표가 실장 이하 전용 메뉴에 접근하는 것을 차단합니다.
 *
 * V136.1 변경사항:
 * - 직책 정보 없으면 MEMBER로 처리 → 제한 메뉴 접근 불가
 * - 로딩 중에도 제한 메뉴 접근 차단
 *
 * V137.2 변경사항:
 * - system_admin이면 모든 경로 접근 허용
 * - useOrgManagementPolicy의 isBlockedByPolicy가 system_admin 처리
 *
 * V148.1 변경사항:
 * - role_exec가 work-status/leave 접근 시 차단
 * - members/projects는 허용
 *
 * 사용 방법:
 * <Route
 *   path="org-management/work-status"
 *   element={
 *     <OrgManagementRouteGuard>
 *       <OrgWorkStatusPage />
 *     </OrgManagementRouteGuard>
 *   }
 * />
 */

import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useOrgManagementPolicy } from '../shared/hooks/useOrgManagementPolicy';
import { useAuthContext } from '../shared/hooks/useAuthContext'; // V148.1: SSoT 기반 역할 체크
import { hasRole } from '../utils/rbac'; // V148.1: hasRole 추가

interface OrgManagementRouteGuardProps {
  children: React.ReactElement;
}

export function OrgManagementRouteGuard({ children }: OrgManagementRouteGuardProps) {
  const location = useLocation();
  const authContext = useAuthContext(); // V148.1: SSoT 기반 역할 체크용
  const {
    isLoading,
    isBlockedByPolicy,
    userJobTitle,
    userJobTitleLevel,
    userJobTitleCode, // V136.1: 직책 코드 추가
    isSystemAdmin, // V137.2: system_admin 여부
  } = useOrgManagementPolicy();

  // V148.1: role_exec 여부 확인 (auth_roleAssignments 기반 SSoT)
  const isRoleExec = hasRole(authContext, 'role_exec');

  // 정책 로딩 중
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        color: 'var(--color-gray-500)',
      }}>
        권한 확인 중...
      </div>
    );
  }

  // V203.1: system_admin은 모든 경로 접근 허용 (role_exec 체크보다 먼저)
  if (isSystemAdmin) {
    return children;
  }

  // V148.1: role_exec가 work-status/leave에 직접 접근 시 차단
  // V202: interviews 추가 (startsWith로 하위 경로 포함 차단)
  const deniedPrefixesForRoleExec = ['/org-management/work-status', '/org-management/leave', '/org-management/interviews'];
  if (isRoleExec && deniedPrefixesForRoleExec.some(prefix => location.pathname.startsWith(prefix))) {
    console.warn(
      `[OrgManagementRouteGuard] V148.1 role_exec 접근 차단: 경로=${location.pathname}`
    );

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px',
        }}>
          🔒
        </div>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '12px',
        }}>
          접근 권한이 없습니다
        </h2>
        <p style={{
          color: 'var(--color-gray-600)',
          marginBottom: '8px',
        }}>
          이 페이지는 조직장 또는 인사팀 담당자만 접근할 수 있습니다.
        </p>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 24px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            뒤로 가기
          </button>
        </div>
      </div>
    );
  }

  // 정책에 의해 차단됨
  // V137.2: system_admin은 isBlockedByPolicy가 항상 false를 반환하므로 이 블록에 진입하지 않음
  if (isBlockedByPolicy(location.pathname)) {
    console.warn(
      `[OrgManagementRouteGuard] 접근 차단: 경로=${location.pathname}, ` +
      `직책=${userJobTitle || '없음'}, 코드=${userJobTitleCode || '없음'}, 레벨=${userJobTitleLevel || 'MEMBER(기본)'}, ` +
      `isSystemAdmin=${isSystemAdmin}`
    );

    // 접근 권한 없음 페이지로 리다이렉트 또는 알림 표시
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px',
        }}>
          🔒
        </div>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '12px',
        }}>
          접근 권한이 없습니다
        </h2>
        <p style={{
          color: 'var(--color-gray-600)',
          marginBottom: '8px',
        }}>
          이 페이지는 실장 단위까지만 접근할 수 있습니다.
        </p>
        <p style={{
          color: 'var(--color-gray-500)',
          fontSize: '14px',
        }}>
          현재 직책: {userJobTitle || '없음'}
        </p>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 24px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            뒤로 가기
          </button>
        </div>
      </div>
    );
  }

  // 정책 통과 - 자식 컴포넌트 렌더링
  return children;
}
